import { configureStore } from "@reduxjs/toolkit";
import { createBrowserHistory } from "history";
import { UnknownAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { EnvHelper } from "@/common/helpers/env";
import { tenantService } from "@/common/services/tenant";

import { errorHandlingMiddleware } from "./appCommon/errorHandlingMiddleware";
import { authMiddleware } from "./auth/middleware";
import { communicationMiddleware } from "./communication/middleware";
import { profileMiddleware } from "./profile/middleware";
import createRootReducer from "./rootReducer";
import { tenantsMiddleware } from "./tenants/middleware";

const tenantInfo = tenantService.resolveTenant("basepath");
const basename = tenantInfo?.identifier || tenantService.defaultTenantIdentifier;

// TODO: history must be created in separate module
export const history = createBrowserHistory({
  basename: tenantService.strategy === "basepath" ? "/" + basename : undefined,
});

function configureStoreCustom(preloadedState?: any, _history = history) {
  const customMiddlewares = [
    errorHandlingMiddleware,
    authMiddleware,
    tenantsMiddleware,
    profileMiddleware,
    communicationMiddleware,
  ];

  const rootReducer = createRootReducer(_history);

  // use RTK to simplify store setup
  const store = configureStore({
    reducer: rootReducer,
    preloadedState: preloadedState,
    middleware: (getDefaultMiddleware) => {
      const defaultMiddlewares = getDefaultMiddleware({
        thunk: true,
        immutableCheck: true,
        serializableCheck: true,
        actionCreatorCheck: true,
      });
      return defaultMiddlewares.concat(customMiddlewares);
    },
    devTools: process.env.NODE_ENV !== "production" && EnvHelper.isLocalhostAny,
  });

  return store;
}

export const store = configureStoreCustom({}, history);
export default store;

// export type AppRootStateType = ReturnType<typeof store.getState>;
export type AppRootState = ReturnType<ReturnType<typeof createRootReducer>>; // avoid circular type reference between the middleware and store definitions

export type AppDispatch = typeof store.dispatch;
export type AppThunkDispatch = ThunkDispatch<AppRootState, any, UnknownAction>;
export type AppThunkAction<ReturnType = void> = ThunkAction<
  ReturnType,
  AppRootState,
  undefined,
  UnknownAction
>;
